import { Link } from 'react-router-dom'

import type { CitedFact } from '@/api/types'
import { Badge, Icon } from '@/design'
import { drillDownHref } from '@/lib/drilldown'
import { factKindLabel } from '@/lib/insights/factFormat'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { Disclosure } from './Disclosure'
import { FactRow } from './FactRow'

function CitedFactRow({ fact, names }: { fact: CitedFact; names: ReadonlyMap<string, string> }) {
  const { filters } = useGlobalFilters()
  const estimate = fact.basis === 'estimate'
  const inputs = (fact.inputs ?? []).map((id) => names.get(id) ?? id)

  return (
    <FactRow
      kind={fact.kind}
      name={fact.name}
      basis={fact.basis}
      value={fact.value}
      comparison={fact.comparison ?? null}
      periodStart={fact.periodStart}
      periodEnd={fact.periodEnd}
      confidence={fact.confidence}
      badge={estimate ? <Badge tone="warning">Estimate</Badge> : null}
    >
      {estimate ? (
        <dl className="ci-fact__estimate">
          {fact.assumption ? (
            <div>
              <dt>Assumption</dt>
              <dd>{fact.assumption}</dd>
            </div>
          ) : null}
          {inputs.length > 0 ? (
            <div>
              <dt>Worked out from</dt>
              <dd>{inputs.join(', ')}</dd>
            </div>
          ) : null}
          {fact.method ? (
            <div>
              <dt>Rule</dt>
              <dd>
                <code>{fact.method}</code>
              </dd>
            </div>
          ) : null}
        </dl>
      ) : null}
      <Link
        className="ci-fact__source"
        to={drillDownHref(fact.drillDown, filters)}
        aria-label={`Open the source page for ${factKindLabel(fact.kind)}, ${fact.name}`}
      >
        Open the source page <Icon name="arrowRight" size={12} />
      </Link>
    </FactRow>
  )
}

/**
 * The facts a digest cites, behind a disclosure (ADR-0009 D9.7): each with its
 * value as the API rendered it and a link to the page it came from. An
 * estimate (D9.11) is badged, with its range (in its value), its assumption
 * and the measured facts it was worked out from.
 */
export function FactsDrawer({ facts }: { facts: readonly CitedFact[] }) {
  if (facts.length === 0) return null
  const names = new Map(facts.map((fact) => [fact.id, `${factKindLabel(fact.kind)} (${fact.name})`]))
  return (
    <Disclosure
      showLabel={`Show the facts used (${facts.length})`}
      hideLabel="Hide the facts used"
      listLabel="Facts used"
    >
      {facts.map((fact) => (
        <CitedFactRow key={fact.id} fact={fact} names={names} />
      ))}
    </Disclosure>
  )
}
